import React from "react";
import { IntentChip } from "@/pages/Inbox";
import {
  Reply, Send, Phone, Calendar, CheckSquare, Flag, MessageSquare, Mail,
  Trophy, XCircle, FolderPlus, StickyNote, FolderSymlink,
} from "lucide-react";

const ACT_ICON = { call: Phone, meeting: Calendar, email: Mail, deadline: Flag, task: CheckSquare };
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const dayKey = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "unknown" : d.toDateString();
};
const dayLabel = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return "Undated";
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yest.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    year: d.getFullYear() === today.getFullYear() ? undefined : "numeric",
  });
};
const timeLabel = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

// Small project tag used in the client-level rollup so you can tell which pursuit a bubble belongs to.
function ProjectChip({ it }) {
  if (it.unassigned) {
    return <span className="text-[10px] font-medium text-[#8a6d00] bg-[#F4C400]/15 border border-[#F4C400]/30 rounded px-1.5 py-0.5">Unassigned</span>;
  }
  if (it.project) {
    return <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[#003E87] bg-[#003E87]/8 border border-[#003E87]/15 rounded px-1.5 py-0.5 max-w-[160px] truncate" title={it.project}><FolderSymlink className="w-2.5 h-2.5 shrink-0" /> {it.project}</span>;
  }
  return null;
}

// One message as a clean iMessage bubble — inbound (client) left, outbound (team) right.
// Deliberately plain: WHO (name · company) said WHAT (one-line recap) WHEN — no raw email, no expand.
function EmailBubble({ it, onToggle, onMove }) {
  const inbound = it.direction === "in";
  const text = it.summary || it.subject || "(no message)";
  const name = it.sender_name || (inbound ? (it.counterparty || "Client") : (it.mine ? "You" : "Teammate"));
  const company = it.sender_company;
  return (
    <div className={`flex ${inbound ? "justify-start mr-10 sm:mr-24" : "justify-end ml-10 sm:ml-24"}`} data-testid={`ctx-email-${it.id}`}>
      <div className={`max-w-[85%] flex flex-col ${inbound ? "items-start" : "items-end"}`}>
        <div className={`flex items-center gap-1.5 mb-1 px-1 flex-wrap ${inbound ? "" : "flex-row-reverse"}`}>
          <span className="text-[11px] font-semibold text-slate-600 truncate max-w-[180px]" data-testid={`ctx-email-who-${it.id}`}>{name}</span>
          {company && <span className="text-[11px] text-slate-400 truncate max-w-[150px]">· {company}</span>}
          {it.intent && <IntentChip intent={it.intent} />}
          <ProjectChip it={it} />
          {it.team && (
            <span data-testid={`ctx-team-${it.id}`} className="text-[10px] font-semibold text-[#003E87] bg-[#003E87]/10 rounded px-1.5 py-0.5">Team</span>
          )}
        </div>

        <div className={inbound
          ? "bg-slate-100 text-slate-800 rounded-2xl rounded-bl-md px-3.5 py-2.5 border border-slate-200/70 shadow-sm"
          : "bg-[#003E87] text-white rounded-2xl rounded-br-md px-3.5 py-2.5 shadow-sm"}>
          <p className="text-sm leading-relaxed break-words">{text}</p>
        </div>

        <div className={`flex items-center gap-2 mt-1 px-1 ${inbound ? "" : "flex-row-reverse"}`}>
          <span className="text-[10px] text-slate-400">{it.date ? timeLabel(it.date) : ""}</span>
          {it.mine && onToggle && (
            <button onClick={() => onToggle(it)} data-testid={`ctx-team-toggle-${it.id}`}
              className="text-[10px] text-slate-400 hover:text-[#003E87] hover:underline">
              {it.team ? "Unshare" : "Share with team"}
            </button>
          )}
          {onMove && it.thread_id && (
            <button onClick={() => onMove(it)} data-testid={`ctx-thread-move-${it.id}`}
              className="text-[10px] text-slate-400 hover:text-[#003E87] hover:underline">
              Move
            </button>
          )}
          {it.team && !it.mine && <span className="text-[10px] text-slate-400 italic">shared by teammate</span>}
        </div>
      </div>
    </div>
  );
}

// Activities, comments and lifecycle events → centered iMessage system chips.
function SystemChip({ it }) {
  let Icon = Flag;
  let text = it.label;
  let sub = null;
  let meta = null;
  if (it.type === "activity") {
    Icon = ACT_ICON[it.kind] || CheckSquare;
    text = `${cap(it.kind)}: ${it.subject}`;
    sub = it.note || null;
    meta = it.owner || null;
  } else if (it.type === "comment") {
    Icon = MessageSquare;
    text = it.text;
    meta = it.author || null;
  } else if (it.type === "event") {
    if (/won/i.test(it.label)) Icon = Trophy;
    else if (/lost/i.test(it.label)) Icon = XCircle;
    else if (/sent/i.test(it.label)) Icon = Send;
    else if (/created/i.test(it.label)) Icon = FolderPlus;
    else Icon = StickyNote;
    text = it.label;
  }
  const tail = [meta, it.project].filter(Boolean).join(" · ");
  return (
    <div className="flex justify-center my-2.5" data-testid={`ctx-item-${it.type}-${it.id}`}>
      <div className="flex flex-col items-center gap-0.5 max-w-[90%] text-center">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200/70 shadow-sm">
          <Icon className="w-3.5 h-3.5 shrink-0 text-slate-400" />
          <span className="whitespace-pre-wrap break-words">{text}</span>
          {it.done && <span className="text-[10px] text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5 shrink-0">done</span>}
        </div>
        {(sub || tail) && (
          <span className="text-[10px] text-slate-400">
            {tail}{sub && tail ? " — " : ""}{sub || ""}
          </span>
        )}
      </div>
    </div>
  );
}

// Unified, chronological context stream styled as an Apple Messages conversation.
export default function ContextTimeline({ items, onToggleTeam, onMove }) {
  if (!items?.length) {
    return (
      <div className="text-sm text-slate-400 py-14 text-center" data-testid="ctx-empty">
        No context logged yet. Emails, activities and notes will appear here as they happen.
      </div>
    );
  }
  const asc = [...items].sort((a, b) => ((a.date || "") < (b.date || "") ? -1 : 1));
  const groups = [];
  let cur = null;
  for (const it of asc) {
    const k = dayKey(it.date);
    if (!cur || cur.key !== k) {
      cur = { key: k, label: dayLabel(it.date), items: [] };
      groups.push(cur);
    }
    cur.items.push(it);
  }
  return (
    <div className="space-y-1 px-1" data-testid="context-timeline">
      {groups.map((g) => (
        <div key={g.key} className="space-y-2">
          <div className="flex items-center justify-center my-5">
            <span className="px-3 py-0.5 text-[10px] font-semibold tracking-wider uppercase text-slate-400 bg-slate-100 rounded-full">{g.label}</span>
          </div>
          {g.items.map((it) =>
            it.type === "email"
              ? <EmailBubble key={`email-${it.id}`} it={it} onToggle={onToggleTeam} onMove={onMove} />
              : <SystemChip key={`${it.type}-${it.id}`} it={it} />
          )}
        </div>
      ))}
    </div>
  );
}
